import {cloneDeep} from 'lodash';
import type {StyleSpecification} from 'mapbox-gl';
import mapStyles, {type StyleVariant, getHumboLayers, getPinsLayers} from './mapStyles';
import {placesToFeatureCollection} from './mapUtils';

export type StyleWithImports = StyleSpecification & {
  imports: [{data: StyleSpecification; config: {lightPreset: string}}];
};

export const getBaseStyle = (style: StyleWithImports) => {
  style.imports[0].data.glyphs = 'mapbox://fonts/honason/{fontstack}/{range}.pbf';
  style.glyphs = 'mapbox://fonts/honason/{fontstack}/{range}.pbf';

  style.imports[0].data.layers = style.imports[0].data.layers.map((layer: any) => {
    if (layer.layout?.['text-font']) {
      if (layer.layout['text-font'][1] === 'Arial Unicode MS Bold') {
        layer.layout['text-font'] = ['Nunito SemiBold'];
      } else {
        layer.layout['text-font'] = ['Nunito Regular'];
      }
    }

    return layer;
  });

  style.sources = {
    ...style.sources,
    place_label: {type: 'vector', url: 'mapbox://mapbox.mapbox-streets-v8'},
    places: {type: 'geojson', data: {type: 'FeatureCollection', features: []}, cluster: true, clusterRadius: 32},
    selectedPlaces: {type: 'geojson', data: {type: 'FeatureCollection', features: []}}
  };

  return style;
};

const getLayerWithOverrides = (humboStyle: StyleVariant, layer: any, theme: 'light' | 'dark') => {
  const layerOverrides = humboStyle.overrides[layer.id];

  if (!layerOverrides) return layer;

  return {
    ...layer,
    paint: {...layer.paint, ...layerOverrides.paint?.[theme]},
    layout: {...layer.layout, ...layerOverrides.layout},
    ...layerOverrides.base
  };
};

type GetVariantOptions = {
  baseStyle: StyleWithImports;
  variant: 'countries' | 'places';
  isDark: boolean;
  highlightedCountryCodes: string[] | undefined;
  places: Partial<Place>[];
};

export const getVariantStyle = ({baseStyle, variant, isDark, highlightedCountryCodes, places}: GetVariantOptions) => {
  const style = cloneDeep(baseStyle);

  const importStyle = style.imports[0].data;
  const humboStyle = mapStyles[variant];

  style.imports[0].config = {...style.imports[0].config, lightPreset: isDark && variant === 'places' ? 'dusk' : 'day'};

  const theme = isDark ? 'dark' : 'light';

  importStyle.layers = humboStyle.importLayers
    .map((layerId) => importStyle.layers.find((layer: any) => layer.id === layerId))
    .map((layer: any) => {
      return getLayerWithOverrides(humboStyle, layer, theme);
    });

  style.layers = [...style.layers, ...getHumboLayers(isDark), ...getPinsLayers(isDark)];

  style.layers = style.layers.map((layer: any) => {
    let layerWithOverrides = {...layer};

    if (layerWithOverrides.id === 'country-boundaries') {
      if (variant === 'places' || !highlightedCountryCodes?.length)
        return {...layerWithOverrides, layout: {...layerWithOverrides.layout, visibility: 'none'}};

      return {
        ...layerWithOverrides,
        filter: [
          'all',
          ['match', ['get', 'iso_3166_1'], highlightedCountryCodes.map((code) => code.toUpperCase()), true, false],
          // Don't show country boundaries according to russian worldview
          ['match', ['get', 'worldview'], ['RU'], false, true]
        ],
        paint: {
          ...layerWithOverrides.paint,
          'fill-color': isDark ? 'rgba(142, 29, 197, 1)' : 'rgba(101, 39, 131, 1)',
          'fill-emissive-strength': 1
        }
      };
    }

    if (layerWithOverrides.id === 'country-label' && highlightedCountryCodes && variant === 'countries') {
      layerWithOverrides = {
        ...layerWithOverrides,
        layout: {...layerWithOverrides.layout, visibility: 'none'},
        filter: [
          ...layerWithOverrides.filter,
          [
            'match',
            ['get', 'iso_3166_1'],
            [...highlightedCountryCodes.map((code) => code.toUpperCase()), 'NON_EXISTENT_COUNTRY_CODE'],
            true,
            false
          ]
        ]
      };
    }

    if (['settlement-major-label', 'settlement-minor-label'].includes(layerWithOverrides.id)) {
      if (variant === 'countries') {
        layerWithOverrides = {...layerWithOverrides, layout: {...layerWithOverrides.layout, visibility: 'none'}};
      }
    }

    return getLayerWithOverrides(humboStyle, layerWithOverrides, theme);
  });

  style.sources = {
    ...style.sources,
    places: {
      type: 'geojson',
      data: placesToFeatureCollection(variant === 'countries' ? [] : places),
      cluster: true,
      clusterRadius: 32
    }
  };

  style.fog = {...style.fog, ...humboStyle.fog[theme]};
  importStyle.lights = humboStyle.lights[theme];

  return style;
};
